import { Avatar, Chip, List, ListItem, ListItemAvatar, ListItemText } from '@mui/material';
import { StyledComponentProps } from "@mui/styles";
import * as api from "api/Knowledge";
import { FeedClassKey, GlobalClassKeys } from "assets/jss/common-styles";
import BoxDrawer from 'components/BoxDrawer';
import ButtonSave from "components/Buttons/ButtonSave";
import IconButtonAdd from "components/Buttons/IconButtonAdd";
import IconButtonOverflowH from "components/Buttons/IconButtonOverflowV";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiPaper from "components/MuiExtras/MuiPaper";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import AppMessage from 'components/Translations/AppMessage';
import { SystemRoles } from "models/ApiEnums";
import { FeedItemDto, TopicSuggestionDto } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from 'react-i18next';
import TopicAutoComplete from "views/Topic/Components/TopicAutoComplete";

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    showTopicsDialog: boolean;
    editTopicDialog: boolean;
    newTopics: TopicSuggestionDto[];
}

interface IProps {
    data: FeedItemDto;
    skipTopicAccessCheckFor?: SystemRoles;
    mouseOver: boolean;
    onTopicClick?: (topicId: string) => void;
    onUpdateCard: (newItemDto: DeepPartial<FeedItemDto>) => void;
}

type Props = IProps & StyledComponentProps<FeedClassKey> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

class InlineTopicEditDrawer extends React.Component<Props, IState> {
    constructor(props: Props) {
        super(props);
        this.state = {
            newTopics: [],
            showTopicsDialog: false,
            editTopicDialog: false
        };
    }

    ///
    private deleteTopic(publicationId: string, topicId: string) {
        api.deleteTopicFromPublication(publicationId, topicId)
            .then((result) => {
                this.props.onUpdateCard({ publicationId, publication: { topics: result } });
                this.props.snackbar.showMessage(this.props.t("Common.Feed.Messages.TopicRemovedFromPublicationSuccess"), "success");
            })
            .catch(() => {
                this.props.snackbar.showMessage(<AppMessage resource="OperationFailed" />, "error");
            });

    }

    ///
    private updateTopics(publicationId: string, topics: TopicSuggestionDto[]) {
        api.updatePublicationTopics(publicationId, topics.map((v) => v.key))
            .then((result) => {
                this.setState({ editTopicDialog: false });
                this.props.onUpdateCard({ publicationId, publication: { topics: result } });
                this.props.snackbar.showMessage(this.props.t("Common.Feed.Messages.PublicationUpdatedSuccess"), "success");
            })
            .catch(() => {
                this.setState(
                    { editTopicDialog: false },
                    () => this.props.snackbar.showMessage(<AppMessage resource="OperationFailed" />, "error")
                );
            });
    }

    ///
    private editTopicsDialog() {
        return (
            <BoxDrawer
                open
                classes={{ paper: this.props.classes.drawerPaper }}
                title={this.props.t("Views.Timeline.Dialogs.InlineTopicEditDrawer.BoxEditTitle")}
                onClose={
                    () => { this.setState({ editTopicDialog: false, newTopics: [] }); }
                }
            >
                <MuiPaper dense elevation={0}>

                    <MuiContainer margin="dense">
                        <TopicAutoComplete
                            autoFocus
                            overModal
                            skipTopicAccessCheckFor={this.props.skipTopicAccessCheckFor}
                            selectedItems={this.state.newTopics}
                            onChange={
                                (topics) => {
                                    this.setState({ newTopics: topics });
                                }
                            }
                        />
                    </MuiContainer>

                    <div className={GlobalClassKeys.textRight}>
                        <ButtonSave
                            color="secondary"
                            onClick={() => {
                                this.updateTopics(this.props.data.publicationId, this.state.newTopics);
                            }}
                        />
                    </div>
                </MuiPaper>

            </BoxDrawer>
        );
    }

    ///
    private showAllTopicsDialog() {
        return (
            <BoxDrawer
                open
                classes={{ paper: this.props.classes.drawerPaper }}
                title={this.props.t("Views.Timeline.Dialogs.InlineTopicEditDrawer.BoxTitle")}
                onClose={
                    () => { this.setState({ showTopicsDialog: false }); }
                }
            >
                <MuiPaper dense elevation={0}>
                    <List>
                        {
                            this.props.data.publication.topics.map((v, index) =>
                                <ListItem key={index}>
                                    <ListItemAvatar><Avatar><img src={v.thumbnailUrl} alt={v.label} width={32} height={32} /></Avatar></ListItemAvatar>
                                    <ListItemText primary={v.label} />
                                </ListItem>
                            )
                        }
                    </List>
                </MuiPaper>
            </BoxDrawer>
        );
    }

    ///
    public render() {
        const { data: { publicationId, publication, permissions }, mouseOver, onTopicClick } = this.props;

        return (
            <>
                {this.state.showTopicsDialog && this.showAllTopicsDialog()}

                {this.state.editTopicDialog && this.editTopicsDialog()}

                {
                    publication.topics.map((v, index) => {
                        const deleteFunc = mouseOver && permissions.canEdit && publication.topics.length > 1 ? () => { this.deleteTopic(publicationId, v.key); } : null;

                        return (index < 3)
                            ? (
                                <React.Fragment key={v.key}>
                                    <Chip
                                        tabIndex={-1}
                                        size="small"
                                        label={v.label}
                                        onClick={() => { onTopicClick && onTopicClick(v.key); }}
                                        onDelete={deleteFunc}
                                    />
                                    <span>&nbsp;</span>
                                </React.Fragment>
                            )
                            : null;
                    })
                }

                {
                    publication.topics.length > 3 &&
                    <IconButtonOverflowH
                        size="small"
                        onClick={() => { this.setState({ showTopicsDialog: true }); }}
                    />
                }

                {
                    permissions.canEdit && mouseOver &&
                    <IconButtonAdd
                        size="small"
                        onClick={
                            () => {
                                this.setState({
                                    editTopicDialog: true,
                                    newTopics: this.props.data.publication.topics
                                });
                            }
                        }
                    />
                }
            </>
        );
    }
}

export default withSnackbar(withTranslation()(InlineTopicEditDrawer));